import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtPrice, isDemoMode } from "@/components/DemoHelpers";
import { FlexiblePriceWizard } from "@/components/FlexiblePriceWizard";
import { ownerDisplayName } from "@/lib/ownerDisplay";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

/**
 * Stage "quote": genera un preventivo PDF con margine sopra il prezzo pro.
 *
 * Iter140d: se il servizio ha `has_flexible_price=True` mostra il
 * FlexiblePriceWizard prima del form di preventivo. Il prezzo di riferimento
 * per il PRO ("Prezzo per te") diventa `adjustedProfessionalPrice` (base
 * professional + maggiorazioni del Prezzo Flessibile). Il bottone "Scarica
 * PDF" resta disabilitato finché tutte le domande non hanno risposta
 * (`flexReady=false`).
 */
export default function QuoteStage({
  selected,
  properties,
  form,
  quoteForm,
  generatingQuote,
  onPatchQuoteForm,
  onBack,
  onGenerate,
  // Iter140d — prezzo flessibile
  pricingAnswers = [],
  setPricingAnswers = () => {},
  adjustedProfessionalPrice,
  flexReady = true,
  housingFeeRate = 0.3,
}) {
  const [feeHintOpen, setFeeHintOpen] = useState(false);
  const hasFlex =
    !!selected?.has_flexible_price &&
    Array.isArray(selected?.pricing_logic) &&
    selected.pricing_logic.length > 0;

  const net = Math.max(0, Number(quoteForm.price_excl_vat) || 0);
  const rate = 22;
  const vat = +((net * rate) / 100).toFixed(2);
  const total = +(net + vat).toFixed(2);
  // Iter140d: usa `adjustedProfessionalPrice` (con maggiorazioni Prezzo
  // Flessibile) come riferimento per il calcolo del margine.
  const proPrice =
    typeof adjustedProfessionalPrice === "number"
      ? adjustedProfessionalPrice
      : Number(selected.professional_price) || 0;
  const margin = +(net - proPrice).toFixed(2);
  const prop = properties.find((p) => p._id === form.property_id);

  // Iter140d: al variare di `adjustedProfessionalPrice` (nuove risposte al
  // wizard), se il campo prezzo è vuoto o pari al vecchio pro price lo
  // pre-riempiamo col nuovo valore, così il PRO non deve ricalcolare a mano.
  useEffect(() => {
    if (!hasFlex) return;
    const current = Number(quoteForm.price_excl_vat) || 0;
    if (current === 0 && proPrice > 0) {
      onPatchQuoteForm({ price_excl_vat: proPrice.toFixed(2) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proPrice]);

  return (
    <div className="space-y-3 py-2">
      <div className="p-3 bg-purple-50 border border-purple-200 rounded-md text-xs">
        <div>
          <strong>Immobile:</strong> {prop ? `${prop.address}, ${prop.city}` : "—"}
        </div>
        <div>
          <strong>Intestatario:</strong> {ownerDisplayName(prop) || "—"}
        </div>
      </div>

      {/* Iter140d: wizard Prezzo Flessibile nel flusso preventivi */}
      {hasFlex && (
        <FlexiblePriceWizard
          pricingLogic={selected.pricing_logic}
          basePrice={Number(selected.professional_price) || Number(selected.base_price) || 0}
          pricingAnswers={pricingAnswers}
          setPricingAnswers={setPricingAnswers}
          onDeltaChange={() => { /* delta è già rispecchiato in adjustedProfessionalPrice */ }}
        />
      )}

      <div>
        <Label>Prezzo per il cliente (imponibile, IVA esclusa) *</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={quoteForm.price_excl_vat}
          onChange={(e) => onPatchQuoteForm({ price_excl_vat: e.target.value })}
          onFocus={() => setFeeHintOpen(true)}
          onClick={() => setFeeHintOpen(true)}
          onBlur={() => setFeeHintOpen(false)}
          data-testid="quote-price-input"
          placeholder="es. 250.00"
        />
        {feeHintOpen && (
          <div
            className="mt-2 rounded-md border border-sky-300 bg-sky-50 text-sky-900 text-xs px-3 py-2 leading-relaxed fade-up"
            role="status"
            data-testid="quote-price-fee-hint"
          >
            ⚠️ Attenzione: se prevedi di condividere il link di pagamento con il tuo cliente, ricordati di calcolare la maggiorazione del {Math.round(housingFeeRate * 1000) / 10}% sul prezzo base di {fmtPrice(proPrice)}.
          </div>
        )}
        <div className="mt-2 flex items-center gap-2 text-xs flex-wrap" data-testid="quote-margin-summary">
          <span className="text-muted-foreground">Prezzo per te:</span>
          <span className="font-mono font-semibold">{fmtPrice(proPrice)}</span>
          <span className="text-purple-500">→</span>
          <span className="text-muted-foreground">Prezzo finale:</span>
          <span className="font-mono font-semibold text-purple-700">{fmt(net)}</span>
          <span
            className={`px-1.5 py-0.5 rounded font-mono ${margin >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
          >
            Margine: {margin >= 0 ? "+" : ""}
            {isDemoMode() ? "€ ***" : fmt(margin)}
          </span>
        </div>
      </div>
      <div>
        <Label>Note aggiuntive (opzionali)</Label>
        <Textarea
          rows={2}
          value={quoteForm.notes}
          onChange={(e) => onPatchQuoteForm({ notes: e.target.value })}
          data-testid="quote-notes-input"
          placeholder="Termini di consegna, condizioni particolari, validità del preventivo…"
        />
      </div>
      <div className="p-3 bg-[color:hsl(var(--surface-alt))] rounded-md text-sm space-y-1">
        <div className="flex justify-between">
          <span>Imponibile</span>
          <span className="font-mono">{fmt(net)}</span>
        </div>
        <div className="flex justify-between">
          <span>IVA {rate}%</span>
          <span className="font-mono">{fmt(vat)}</span>
        </div>
        <div className="flex justify-between pt-2 border-t font-bold text-purple-700 text-base">
          <span>Totale (IVA inclusa)</span>
          <span className="font-mono">{fmt(total)}</span>
        </div>
      </div>
      {hasFlex && !flexReady && (
        <div
          className="p-2 border border-amber-300 bg-amber-50 rounded text-xs text-amber-900"
          data-testid="quote-flex-incomplete-warning"
        >
          ⚠️ Completa tutte le domande del Prezzo Flessibile per generare il preventivo.
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <Button variant="ghost" onClick={onBack} data-testid="quote-stage-back-btn">
          ← Indietro
        </Button>
        <Button
          className="flex-1 ht-gradient border-0"
          disabled={generatingQuote || net <= 0 || (hasFlex && !flexReady)}
          onClick={onGenerate}
          data-testid="quote-generate-btn"
        >
          {generatingQuote ? "Generazione…" : "📄 Scarica PDF"}
        </Button>
      </div>
    </div>
  );
}
